<template>
  <div class="associates-page">
    <page-loading
      v-if="loading"
      :message="isNetworkHub ? 'Carregando sua rede' : 'Carregando associados'"
      :subtitle="isNetworkHub ? 'Montando visão completa até o 5º nível' : 'Montando a lista'"
    />

    <template v-else>
      <!-- ─── Cabeçalho ─── -->
      <div class="page-header">
        <div>
          <h1 class="page-header__title page-header__title-row">
            <span>{{ pageTitle }}</span>
            <premium-member-badge
              v-if="isAssociatePremiumFromStore"
              class="ml-2"
              size="x-small"
            />
          </h1>
          <p class="page-header__subtitle">{{ pageSubtitle }}</p>
          <v-select
            v-if="isNetworkHub && showFranchiseFilter"
            v-model="franchiseId"
            :items="adminFranchiseOptions"
            item-text="label"
            item-value="id"
            label="Franquia"
            dense
            outlined
            hide-details
            class="franchise-select mt-3"
            @change="reloadNetworkHub"
          />
        </div>
        <div class="page-header__actions">
          <v-btn
            v-if="isNetworkHub && dashboard"
            outlined
            small
            color="#e5183d"
            class="mr-2"
            :loading="refreshing"
            @click="reloadNetworkHub"
          >
            <v-icon left small>mdi-refresh</v-icon>
            Atualizar
          </v-btn>
          <v-btn
            v-if="canShowRegisterButton"
            color="#e5183d"
            dark
            rounded
            @click="openNewAssociateDialog"
          >
            <v-icon left>mdi-account-plus</v-icon>
            Cadastrar associado
          </v-btn>
        </div>
      </div>

      <!-- ─── Hub da rede (perfil associado) ─── -->
      <template v-if="isNetworkHub && dashboard">
        <referral-link-card :franchise-id="effectiveFranchiseId" />

        <premium-renewal-alert
          :show="showPremiumRenewalAlert"
          :expired="!!dashboard.isExpired"
          :days-until-expiry="dashboard.daysUntilExpiry ?? null"
          :expires-at="dashboard.expiresAt"
          @renew="openPremiumModal"
        />

        <pag-bank-premium-alert
          :show="showPagbankAlert"
          :show-action="!isPagbankConnected"
        />

        <!-- CTA Premium (não Premium) -->
        <premium-upsell-banner
          v-if="canSubscribeToPremium && !showPremiumRenewalAlert"
          class="mb-4"
          variant="hero"
          :potential-earnings="dashboard.totalPotentialEarnings"
          title="Sua rede já existe — falta desbloquear os ganhos"
          subtitle="Você cadastra associados normalmente, mas só recebe comissões de adesão Premium quando também é Premium."
          cta-label="Aderir ao Premium — R$ 297"
          @cta="openPremiumModal"
          @learn-more="scrollToHowItWorks"
        />

        <!-- Status Premium ativo / admin -->
        <v-card v-else class="hub-status-card mb-4" outlined>
          <div class="hub-status-card__inner">
            <v-icon
              :color="dashboard.status === 'ACTIVE' ? '#2dcc6b' : '#e5183d'"
              size="32"
            >
              {{
                dashboard.status === "ACTIVE"
                  ? "mdi-crown"
                  : dashboard.isExpired
                    ? "mdi-crown-outline"
                    : "mdi-account-supervisor"
              }}
            </v-icon>
            <div>
              <strong class="hub-status-card__title">{{ premiumStripTitle }}</strong>
              <div class="hub-status-card__detail">
                <span v-if="dashboard.joinedAt">Premium desde {{ formatDate(dashboard.joinedAt) }}</span>
                <span v-if="dashboard.expiresAt && dashboard.status === 'ACTIVE'">
                  <span v-if="dashboard.joinedAt" class="mx-2">·</span>
                  Válido até {{ formatDate(dashboard.expiresAt) }}
                </span>
                <span v-else-if="dashboard.viewerRole === 'franchise_admin' && !dashboard.joinedAt">
                  Visão completa da rede da franquia em todos os níveis
                </span>
                <template v-if="dashboard.canSubscribePremium !== false">
                  <span class="mx-2">·</span>
                    PagBank: {{ isPagbankConnected ? "Conectado" : "Não conectado" }}
                </template>
              </div>
            </div>
          </div>
        </v-card>

        <!-- KPIs -->
        <v-row class="mb-4" dense>
          <v-col v-for="metric in getHubMetrics()" :key="metric.label" cols="6" sm="3">
            <v-card
              outlined
              class="metric-tile-card pa-3"
              :class="{ 'metric-tile-card--highlight': metric.highlight }"
            >
              <v-icon small :color="metric.color" class="mb-1">{{ metric.icon }}</v-icon>
              <span class="metric-tile__label">{{ metric.label }}</span>
              <strong class="metric-tile__value">{{ metric.value }}</strong>
              <span v-if="metric.locked" class="metric-tile__locked">
                <v-icon x-small>mdi-lock</v-icon> Desbloqueie com Premium
              </span>
            </v-card>
          </v-col>
        </v-row>

        <v-card
          v-if="voucherInfo?.hasPremiumVoucher"
          outlined
          class="mb-4 voucher-card"
        >
          <v-card-text class="d-flex flex-wrap align-center justify-space-between">
            <div>
              <div class="voucher-card__title">
                <v-icon color="#e5183d" class="mr-2">mdi-ticket-percent</v-icon>
                Vouchers Premium
              </div>
              <p class="mb-0 mt-2 grey--text text--darken-1">
                {{ voucherInfo.message }}
              </p>
            </div>
            <v-btn
              color="#e5183d"
              dark
              :disabled="!voucherInfo.canDistribute"
              @click="openGrantVoucherDialog"
            >
              Conceder voucher
            </v-btn>
          </v-card-text>
        </v-card>

        <!-- Comissões + rede -->
        <v-card id="premium-how-it-works" outlined class="mb-4 earnings-card">
          <v-tabs v-model="earningsTab" background-color="transparent" color="#e5183d">
            <v-tab>
              <v-icon left small>mdi-cash-multiple</v-icon>
              Comissões por nível
            </v-tab>
            <v-tab>
              <v-icon left small>mdi-account-group</v-icon>
              Sua rede por nível
            </v-tab>
          </v-tabs>
          <v-divider />
          <v-tabs-items v-model="earningsTab">
            <v-tab-item>
              <div class="pa-4">
                <div v-if="canSubscribeToPremium" class="locked-earnings-banner mb-4">
                  <v-icon color="#f57c00">mdi-lock-alert</v-icon>
                  <div>
                    <strong>Ganhos bloqueados</strong>
                    <p>
                      Você tem {{ formatCurrency(dashboard.totalPotentialEarnings) }} de potencial
                      em adesões na rede. Torne-se Premium para receber.
                    </p>
                    <v-btn x-small color="#e5183d" dark @click="openPremiumModal">
                      Desbloquear agora
                    </v-btn>
                  </div>
                </div>
                <premium-commission-ladder
                  :locked="canSubscribeToPremium"
                  :show-received="!canSubscribeToPremium"
                  :received-by-level="earningsByLevelMap"
                />
              </div>
            </v-tab-item>
            <v-tab-item>
              <div class="pa-4">
                <p class="section-hint mb-3">
                  Associados não Premium permanecem na árvore, mas não geram comissão de adesão para você.
                </p>
                <div
                  v-for="item in dashboard.opportunityByLevel"
                  :key="'o-' + item.level"
                  class="level-row level-row--opportunity"
                >
                  <span class="level-row__badge">N{{ item.level }}</span>
                  <div class="level-row__detail">
                    <span>{{ item.totalAssociates }} associados</span>
                    <span class="text-caption grey--text">
                      {{ item.premiumAssociates }} Premium · {{ item.nonPremiumAssociates }} não Premium
                    </span>
                  </div>
                  <div class="level-row__potential">
                    <span class="level-row__potential-label">Potencial</span>
                    <strong
                      class="level-row__value"
                      :class="{ 'level-row__value--locked': canSubscribeToPremium }"
                    >
                      <v-icon v-if="canSubscribeToPremium" x-small>mdi-lock</v-icon>
                      +{{ formatCurrency(item.additionalPotentialEarnings) }}
                    </strong>
                  </div>
                </div>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card>

        <!-- Filtros da rede -->
        <v-card outlined class="pa-4 mb-4 filters-card">
          <v-row dense align="center">
            <v-col cols="12" sm="3">
              <v-text-field
                v-model="networkSearch"
                label="Buscar na rede"
                prepend-inner-icon="mdi-magnify"
                dense
                outlined
                hide-details
                clearable
                placeholder="Nome, e-mail, documento..."
              />
            </v-col>
            <v-col cols="6" sm="2">
              <v-select
                v-model="networkFilters.premiumStatus"
                :items="premiumStatusOptions"
                label="Premium"
                dense
                outlined
                hide-details
                @change="loadNetwork"
              />
            </v-col>
            <v-col cols="6" sm="2">
              <v-select
                v-model="networkFilters.level"
                :items="levelOptions"
                label="Nível"
                dense
                outlined
                hide-details
                clearable
                @change="loadNetwork"
              />
            </v-col>
            <v-col cols="6" sm="2">
              <v-text-field
                v-model="networkFilters.periodStart"
                label="De"
                type="date"
                dense
                outlined
                hide-details
                @change="loadNetwork"
              />
            </v-col>
            <v-col cols="6" sm="2">
              <v-text-field
                v-model="networkFilters.periodEnd"
                label="Até"
                type="date"
                dense
                outlined
                hide-details
                @change="loadNetwork"
              />
            </v-col>
            <v-col cols="12" sm="1" class="d-flex" style="gap: 4px">
              <v-chip small color="#EEF3FF" text-color="#24305E">
                {{ getFilteredNetworkMembers().length }} / {{ network?.totals.total ?? 0 }}
              </v-chip>
            </v-col>
          </v-row>
        </v-card>

        <!-- Rede: árvore + lista -->
        <v-card outlined class="network-card">
          <v-tabs v-model="networkTab" background-color="transparent" color="#e5183d">
            <v-tab>
              <v-icon left small>mdi-file-tree</v-icon>
              Árvore hierárquica
            </v-tab>
            <v-tab>
              <v-icon left small>mdi-format-list-bulleted</v-icon>
              Lista completa
            </v-tab>
          </v-tabs>
          <v-divider />

          <v-tabs-items v-model="networkTab">
            <v-tab-item>
              <div class="pa-4">
                <p class="text-caption grey--text mb-3">
                  Todos os níveis da sua rede (até 5). Premium em verde.
                </p>
                <div v-if="!network?.tree.length" class="empty-state">
                  <v-icon large color="#ccc">mdi-account-group-outline</v-icon>
                  <p>Sua rede ainda não tem associados indicados.</p>
                  <v-btn color="#e5183d" dark small @click="openNewAssociateDialog">
                    Cadastrar primeiro associado
                  </v-btn>
                </div>
                <network-tree-node
                  v-for="node in network?.tree ?? []"
                  :key="node.id"
                  :node="node"
                />
              </div>
            </v-tab-item>

            <v-tab-item>
              <div class="pa-2">
                <v-data-table
                  :headers="networkTableHeaders"
                  :items="getFilteredNetworkMembers()"
                  :items-per-page="15"
                  :footer-props="{ 'items-per-page-text': 'por página' }"
                  dense
                  class="elevation-0 network-table"
                  no-data-text="Nenhum associado na rede com os filtros atuais"
                >
                  <template v-slot:body="{ items }">
                    <tbody>
                      <tr
                        v-for="member in items"
                        :key="member.id"
                        :class="{ 'row--premium': member.isPremiumActive }"
                      >
                        <td>
                          <div class="member-name">{{ member.name }}</div>
                          <div class="member-sub">{{ member.email }}</div>
                        </td>
                        <td>
                          <v-chip x-small :color="levelColor(member.level)" dark class="font-weight-bold">
                            N{{ member.level }}
                          </v-chip>
                        </td>
                        <td>
                          <v-chip
                            x-small
                            :color="member.isPremiumActive ? '#2dcc6b' : '#eeeeee'"
                            :text-color="member.isPremiumActive ? '#fff' : '#616161'"
                          >
                            {{ member.isPremiumActive ? "Premium" : "Não Premium" }}
                          </v-chip>
                        </td>
                        <td>{{ formatDocument(member.document) }}</td>
                        <td>{{ formatPhone(member.phone) }}</td>
                        <td>
                          <v-icon x-small :color="member.pagbankConnected ? '#2dcc6b' : '#bdbdbd'">
                            {{ member.pagbankConnected ? "mdi-check-circle" : "mdi-close-circle" }}
                          </v-icon>
                        </td>
                        <td>{{ member.premiumJoinedAt ? formatDate(member.premiumJoinedAt) : "—" }}</td>
                        <td class="earned-cell">{{ formatCurrency(member.generatedForSponsor) }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-data-table>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card>

        <associate-premium-modal
          v-model="premiumModalOpen"
          :loading="paymentLoading"
          :checking-premium="checkingPremium"
          :customer-document.sync="customerDocument"
          :qrcode-image="qrcodeImage"
          :qrcode-text="qrcodeText"
          :pagbank-connected="isPagbankConnected"
          :potential-earnings="dashboard.totalPotentialEarnings"
          @pay="handlePremiumPayment"
          @checkPremium="reloadNetworkHub"
        />

        <premium-sticky-cta
          v-if="canSubscribeToPremium"
          :potential-earnings="dashboard.totalPotentialEarnings"
          @cta="openPremiumModal"
        />
      </template>

      <!-- ─── Modo franqueado / colaborador ─── -->
      <template v-else>
        <v-card>
          <v-card-title>
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Pesquise por nome, documento ou e-mail"
              dense
              outlined
              hide-details
              class="mt-0"
            />
          </v-card-title>
          <v-data-table
            :headers="headers"
            :items="associates"
            :items-per-page="10"
            :search="search"
            class="elevation-0"
            :no-data-text="`Nenhum ${profileLabelSingular} encontrado`"
          >
            <template v-slot:body="{ items }">
              <tbody>
                <tr v-for="associate in items" :key="associate.id">
                  <td>{{ associate.name }}</td>
                  <td>{{ associate.email }}</td>
                  <td>{{ formatDocument(associate.document) }}</td>
                  <td>{{ formatPhone(associate.phone) }}</td>
                  <td v-if="isCollaboratorMode">{{ formatPercent(associate.transactionPercent) }}</td>
                  <td v-if="isCollaboratorMode">{{ formatPercent(associate.signaturePercent) }}</td>
                  <td v-if="!isFranchiseAssociateInactive" class="actions">
                    <v-icon size="20" color="#e5183d" @click="handleEditAssociate(associate)">
                      mdi-pencil
                    </v-icon>
                    <v-icon size="20" color="#e5183d" @click="handleDeleteAssociate(associate)">
                      mdi-delete
                    </v-icon>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-data-table>
        </v-card>
      </template>

      <v-dialog v-model="grantVoucherOpen" max-width="520">
        <v-card>
          <v-card-title>Conceder voucher Premium</v-card-title>
          <v-card-text>
            <v-select
              v-model="grantVoucherTargetId"
              :items="grantVoucherCandidates"
              item-text="label"
              item-value="id"
              label="Associado beneficiário"
              outlined
              dense
              hide-details
              no-data-text="Nenhum associado não Premium na sua rede"
            />
            <p class="caption mt-3 mb-0 grey--text">
              O associado torna-se Premium imediatamente, sem cobrança e sem comissões para a rede.
            </p>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="grantVoucherOpen = false">Cancelar</v-btn>
            <v-btn
              color="#e5183d"
              dark
              :loading="grantingVoucher"
              :disabled="!grantVoucherTargetId"
              @click="handleGrantVoucher"
            >
              Conceder
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog cadastro -->
      <v-dialog v-model="openNewAssociate" max-width="720">
        <v-card>
          <v-card-title>
            {{ isEditingAssociate ? "Editar associado" : "Novo associado" }}
          </v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field v-model="newAssociate.name" label="Nome completo" outlined dense />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="newAssociate.phone"
                  label="Telefone"
                  outlined
                  dense
                  v-mask="'(##) #####-####'"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="newAssociate.document"
                  label="CPF ou CNPJ"
                  outlined
                  dense
                  v-mask="documentMask"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="newAssociate.email" label="E-mail" type="email" outlined dense />
              </v-col>
              <v-col
                v-if="!isEditingAssociate && voucherInfo?.canDistribute"
                cols="12"
              >
                <v-checkbox
                  v-model="usePremiumVoucher"
                  hide-details
                  class="mt-0"
                  label="Utilizar Voucher Premium"
                  :hint="'Será consumido 1 voucher do seu saldo (' + (voucherInfo?.premiumVoucherBalance ?? 0) + ' disponíveis)'"
                  persistent-hint
                />
              </v-col>
              <template v-if="isCollaboratorMode">
                <v-col cols="6" md="3">
                  <v-text-field
                    v-model="newAssociate.transactionPercent"
                    label="% transação"
                    type="number"
                    outlined
                    dense
                    suffix="%"
                  />
                </v-col>
                <v-col cols="6" md="3">
                  <v-text-field
                    v-model="newAssociate.signaturePercent"
                    label="% assinatura"
                    type="number"
                    outlined
                    dense
                    suffix="%"
                  />
                </v-col>
              </template>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="closeAssociateDialog">Cancelar</v-btn>
            <v-btn color="#e5183d" dark :loading="savingAssociate" @click="handleSaveAssociate">
              {{ isEditingAssociate ? "Atualizar" : "Salvar" }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </div>
</template>

<script lang="ts">
import AssociatePremiumModal from "@/components/premium/AssociatePremiumModal.vue";
import NetworkTreeNode from "@/components/premium/NetworkTreeNode.vue";
import PagBankPremiumAlert from "@/components/premium/PagBankPremiumAlert.vue";
import PremiumCommissionLadder from "@/components/premium/PremiumCommissionLadder.vue";
import PremiumStickyCta from "@/components/premium/PremiumStickyCta.vue";
import PremiumMemberBadge from "@/components/premium/PremiumMemberBadge.vue";
import PremiumUpsellBanner from "@/components/premium/PremiumUpsellBanner.vue";
import ReferralLinkCard from "@/components/referral/ReferralLinkCard.vue";
import PremiumRenewalAlert from "@/components/premium/PremiumRenewalAlert.vue";
import PageLoading from "@/components/loading/PageLoading.vue";
import { CreateAssociatePremiumSignature } from "@/services/premium/CreateAssociatePremiumSignature";
import { GetPremiumDashboard } from "@/services/premium/GetPremiumDashboard";
import {
  GetPremiumNetwork,
  PremiumNetworkFilters,
} from "@/services/premium/GetPremiumNetwork";
import { PremiumDashboardType } from "@/types/premium/PremiumDashboard";
import { PremiumNetworkNode, PremiumNetworkType } from "@/types/premium/PremiumNetwork";
import { buildTransactionQRCodeText } from "@/utils/transactionQrcode";
import { AssociateType } from "@/types/associate/Associate";
import { GetAssociates } from "@/services/associates/GetAssociates";
import { CreateAssociate, CreateAssociatePayload } from "@/services/associates/CreateAssociate";
import { GetPremiumVouchers } from "@/services/premium/GetPremiumVouchers";
import { GrantPremiumVoucher } from "@/services/premium/GrantPremiumVoucher";
import { PremiumVoucherInfo } from "@/types/premium/PremiumVoucher";
import { DeleteAssociate } from "@/services/associates/DeleteAssociate";
import { UpdateAssociate, UpdateAssociatePayload } from "@/services/associates/UpdateAssociate";
import { SessionFranchiseType } from "@/types/user/SessionInfo";
import { format } from "date-fns";
import QRCode from "qrcode";
import { defineComponent } from "vue";
import { mapActions } from "vuex";

const LEVEL_COLORS = ["#e5183d", "#f57c00", "#fbc02d", "#7cb342", "#2dcc6b"];

const formatBrl = (value: number) =>
  Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

export default defineComponent({
  name: "AssociatesView",
  components: {
    PageLoading,
    PagBankPremiumAlert,
    AssociatePremiumModal,
    NetworkTreeNode,
    PremiumCommissionLadder,
    PremiumStickyCta,
    PremiumMemberBadge,
    PremiumUpsellBanner,
    ReferralLinkCard,
    PremiumRenewalAlert,
  },
  data() {
    return {
      loading: true,
      refreshing: false,
      franchiseId: "",
      associates: [] as AssociateType[],
      search: "",
      headers: [] as Array<{ text: string; value: string; sortable?: boolean }>,
      dashboard: null as PremiumDashboardType | null,
      network: null as PremiumNetworkType | null,
      networkTab: 0,
      earningsTab: 0,
      networkSearch: "",
      networkFilters: {
        premiumStatus: "ALL",
        level: undefined as number | undefined,
        periodStart: "",
        periodEnd: "",
      } as PremiumNetworkFilters,
      premiumStatusOptions: [
        { text: "Todos", value: "ALL" },
        { text: "Premium", value: "PREMIUM" },
        { text: "Não Premium", value: "NON_PREMIUM" },
      ],
      levelOptions: [1, 2, 3, 4, 5].map((l) => ({ text: `Nível ${l}`, value: l })),
      networkTableHeaders: [
        { text: "Associado", value: "name" },
        { text: "Nível", value: "level" },
        { text: "Premium", value: "isPremiumActive" },
        { text: "Documento", value: "document" },
        { text: "Telefone", value: "phone" },
        { text: "PagBank", value: "pagbankConnected" },
        { text: "Adesão Premium", value: "premiumJoinedAt" },
        { text: "Gerado p/ você", value: "generatedForSponsor" },
      ],
      premiumModalOpen: false,
      paymentLoading: false,
      checkingPremium: false,
      customerDocument: "",
      qrcodeImage: "",
      qrcodeText: "",
      openNewAssociate: false,
      savingAssociate: false,
      isEditingAssociate: false,
      editingAssociateId: "",
      voucherInfo: null as PremiumVoucherInfo | null,
      usePremiumVoucher: false,
      grantVoucherOpen: false,
      grantVoucherTargetId: "",
      grantingVoucher: false,
      newAssociate: {
        name: "",
        phone: "",
        email: "",
        document: "",
        transactionPercent: null as number | null,
        signaturePercent: null as number | null,
      },
    };
  },
  computed: {
    isAssociateProfile(): boolean {
      return this.$store.getters.isAssociateProfile;
    },
    isFranchiseAdmin(): boolean {
      if (!this.effectiveFranchiseId) return false;
      return this.sessionFranchises.some(
        (franchise) =>
          franchise.id === this.effectiveFranchiseId && franchise.isAdmin
      );
    },
    isNetworkHub(): boolean {
      return this.isAssociateProfile || this.isFranchiseAdmin;
    },
    showFranchiseFilter(): boolean {
      return (
        this.isFranchiseAdmin &&
        !this.$store.getters.hasNoFranchiseFilterProfile &&
        this.sessionFranchises.filter((f) => f.isAdmin).length > 1
      );
    },
    adminFranchiseOptions(): Array<{ id: string; label: string }> {
      return this.sessionFranchises
        .filter((franchise) => franchise.isAdmin)
        .map((franchise) => ({
          id: franchise.id,
          label: franchise.cityName
            ? `${franchise.cityName} - ${franchise.state}`
            : franchise.name || franchise.id,
        }));
    },
    canSubscribeToPremium(): boolean {
      if (this.isFranchiseAdmin && !this.isAssociateProfile) return false;
      if (!this.dashboard) return false;
      if (this.dashboard.canSubscribePremium === false) return false;
      if (this.dashboard.status === "ACTIVE" && !this.dashboard.renewalDueSoon) {
        return false;
      }
      return (
        this.dashboard.status === "INACTIVE" ||
        this.dashboard.status === "EXPIRED" ||
        !!this.dashboard.renewalDueSoon
      );
    },
    showPremiumRenewalAlert(): boolean {
      if (!this.dashboard || this.dashboard.canSubscribePremium === false) {
        return false;
      }
      return !!this.dashboard.renewalDueSoon || !!this.dashboard.isExpired;
    },
    premiumStripTitle(): string {
      if (!this.dashboard) return "";
      if (this.dashboard.isExpired) return "Assinatura Premium expirada";
      if (this.dashboard.status === "ACTIVE" && this.dashboard.renewalDueSoon) {
        return "Premium ativo — renovação em breve";
      }
      if (this.dashboard.status === "ACTIVE") return "Associado Premium ativo";
      if (this.dashboard.viewerRole === "franchise_admin") {
        return "Painel de rede da franquia";
      }
      return "Você ainda não é Premium";
    },
    isAssociateMode(): boolean {
      return this.$route.query.mode === "associate" || this.isAssociateProfile;
    },
    isCollaboratorMode(): boolean {
      return !this.isNetworkHub && this.$route.query.mode !== "associate";
    },
    isFranchiseAssociateInactive(): boolean {
      return this.$store.getters.isFranchiseAssociateInactive;
    },
    canManageAssociates(): boolean {
      return this.$store.getters.isAssociatesRegistrar;
    },
    canShowRegisterButton(): boolean {
      if (this.isFranchiseAssociateInactive) return false;
      return this.canManageAssociates || this.isNetworkHub;
    },
    pageTitle(): string {
      return this.isNetworkHub ? "Minha Rede" : "Associados";
    },
    isAssociatePremiumFromStore(): boolean {
      return this.$store.getters.isAssociatePremiumActive;
    },
    pageSubtitle(): string {
      if (this.isNetworkHub && this.isFranchiseAdmin && !this.isAssociateProfile) {
        return "Acompanhe todos os associados da sua rede de expansão, em todos os níveis, com status Premium e detalhes.";
      }
      if (this.isNetworkHub) {
        return "Visualize todos os associados da sua rede, em todos os níveis, com status Premium e ganhos.";
      }
      return "Gerencie colaboradores e associados da franquia.";
    },
    profileLabelSingular(): string {
      return this.isAssociateMode ? "associado" : "colaborador";
    },
    sessionFranchises(): SessionFranchiseType[] {
      return this.$store.getters.sessionFranchises;
    },
    effectiveFranchiseId(): string {
      return (
        this.franchiseId ||
        this.$store.state.session?.associateFranchiseId ||
        this.sessionFranchises[0]?.id ||
        ""
      );
    },
    documentMask(): string {
      const digits = (this.newAssociate.document ?? "").replace(/\D/g, "");
      return digits.length > 11 ? "##.###.###/####-##" : "###.###.###-##";
    },
    isPagbankConnected(): boolean {
      if (this.dashboard?.pagbankConnected) {
        return true;
      }
      if (this.isFranchiseAdmin && !this.isAssociateProfile) {
        const franchise = this.sessionFranchises.find(
          (item) => item.id === this.effectiveFranchiseId && item.isAdmin
        );
        return !!franchise?.pagbankConnect;
      }
      return !!this.$store.state.session?.pagbankConnect;
    },
    showPagbankAlert(): boolean {
      if (!this.dashboard || this.isPagbankConnected) {
        return false;
      }
      return (
        this.dashboard.hasPendingPagbankEarnings ||
        (this.dashboard.pendingPagbankAmount ?? 0) > 0
      );
    },
    earningsByLevelMap(): Record<number, number> {
      if (!this.dashboard) return {};
      return this.dashboard.earningsByLevel.reduce(
        (acc, item) => {
          acc[item.level] = item.received;
          return acc;
        },
        {} as Record<number, number>
      );
    },
    grantVoucherCandidates(): Array<{ id: string; label: string }> {
      return this.getFilteredNetworkMembers()
        .filter((member) => !member.isPremiumActive)
        .map((member) => ({
          id: member.id,
          label: `${member.name} · ${this.formatDocument(member.document)}`,
        }));
    },
  },
  watch: {
    sessionFranchises: {
      handler() {
        this.initializeFranchiseContext();
      },
      immediate: true,
    },
    "$route.query.adesao": {
      handler(value: string | undefined) {
        if (value === "premium" && this.canSubscribeToPremium) {
          this.openPremiumModal();
        }
      },
      immediate: true,
    },
  },
  created() {
    this.initializeFranchiseContext();

    if (!this.isNetworkHub && !this.canManageCollaborators()) {
      this.$store.dispatch("showSnack", {
        message: "Você não possui permissão para acessar esta área.",
        color: "warning",
      });
      this.$router.replace("/");
      return;
    }

    const baseCollaborator = [
      { text: "Nome", value: "name" },
      { text: "E-mail", value: "email" },
      { text: "Documento", value: "document" },
      { text: "Telefone", value: "phone" },
      { text: "% transação", value: "transactionPercent" },
      { text: "% assinatura", value: "signaturePercent" },
      { text: "Ações", value: "actions", sortable: false },
    ];
    this.headers = baseCollaborator;
    this.prefillCustomerDocument();
    this.loadPage();
  },
  methods: {
    ...mapActions(["showSnack", "loadSession"]),
    canManageCollaborators(): boolean {
      return (
        this.$store.getters.isCollaboratorManager ||
        this.$store.getters.isAssociatesRegistrar
      );
    },
    getHubMetrics() {
      if (!this.dashboard) return [];
      const isLocked = this.canSubscribeToPremium;
      return [
        {
          label: "Recebido da rede",
          value: isLocked ? "R$ 0,00" : formatBrl(this.dashboard.totalReceivedFromNetwork),
          icon: "mdi-cash-multiple",
          color: "#2dcc6b",
          locked: isLocked,
        },
        {
          label: "Total na rede",
          value: String(this.dashboard.totalNetworkAssociates),
          icon: "mdi-account-group",
          color: "#e5183d",
        },
        {
          label: "Premium na rede",
          value: String(this.dashboard.totalPremiumAssociates),
          icon: "mdi-crown",
          color: "#f57c00",
        },
        {
          label: "Potencial adicional",
          value: formatBrl(this.dashboard.totalPotentialEarnings),
          icon: "mdi-trending-up",
          color: "#24305E",
          highlight: isLocked && this.dashboard.totalPotentialEarnings > 0,
          locked: isLocked,
        },
      ];
    },
    prefillCustomerDocument() {
      const doc = this.$store.state.session?.document;
      if (!doc || this.customerDocument) return;
      const digits = String(doc).replace(/\D/g, "");
      if (digits.length === 11) {
        this.customerDocument = digits.replace(
          /(\d{3})(\d{3})(\d{3})(\d{2})/,
          "$1.$2.$3-$4"
        );
      }
    },
    openPremiumModal() {
      this.prefillCustomerDocument();
      this.premiumModalOpen = true;
    },
    async loadVoucherInfo() {
      if (!this.$store.getters.canUsePremiumVouchers) {
        this.voucherInfo = null;
        return;
      }
      try {
        this.voucherInfo = await new GetPremiumVouchers().execute();
      } catch {
        this.voucherInfo = null;
      }
    },
    openGrantVoucherDialog() {
      this.grantVoucherTargetId = "";
      this.grantVoucherOpen = true;
    },
    async handleGrantVoucher() {
      if (!this.grantVoucherTargetId) return;
      this.grantingVoucher = true;
      try {
        await new GrantPremiumVoucher().execute(this.grantVoucherTargetId);
        this.showSnack({ message: "Voucher concedido com sucesso.", color: "success" });
        this.grantVoucherOpen = false;
        await this.reloadNetworkHub();
      } catch {
        this.showSnack({ message: "Não foi possível conceder o voucher.", color: "error" });
      } finally {
        this.grantingVoucher = false;
      }
    },
    scrollToHowItWorks() {
      this.earningsTab = 0;
      this.$nextTick(() => {
        const el = document.getElementById("premium-how-it-works");
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    },
    getFilteredNetworkMembers() {
      const members = this.network?.members ?? [];
      const q = this.networkSearch.trim().toLowerCase();
      if (!q) return members;
      return members.filter(
        (m: PremiumNetworkNode) =>
          m.name?.toLowerCase().includes(q) ||
          m.email?.toLowerCase().includes(q) ||
          m.document?.includes(q.replace(/\D/g, ""))
      );
    },
    initializeFranchiseContext() {
      if (this.franchiseId) return;
      const adminFranchise = this.sessionFranchises.find((f) => f.isAdmin);
      this.franchiseId =
        this.$store.state.session?.associateFranchiseId ||
        adminFranchise?.id ||
        this.sessionFranchises[0]?.id ||
        "";
    },
    async loadPage() {
      this.loading = true;
      try {
        if (this.isNetworkHub) {
          await this.reloadNetworkHub();
        } else {
          await this.handleLoadAssociates();
        }
      } finally {
        this.loading = false;
      }
    },
    async reloadNetworkHub() {
      this.refreshing = true;
      this.checkingPremium = true;
      try {
        const franchiseId = this.effectiveFranchiseId;
        if (!franchiseId) {
          throw new Error("Franchise not found");
        }
        const [dashboard, network] = await Promise.all([
          new GetPremiumDashboard().execute(franchiseId),
          this.loadNetwork(),
          this.loadVoucherInfo(),
        ]);
        this.dashboard = dashboard;
        this.network = network;
        await this.loadSession();
        if (this.$route.query.adesao === "premium" && this.canSubscribeToPremium) {
          this.openPremiumModal();
        }
      } catch {
        this.showSnack({
          message: "Não foi possível carregar sua rede.",
          color: "error",
        });
      } finally {
        this.refreshing = false;
        this.checkingPremium = false;
        this.loading = false;
      }
    },
    async loadNetwork() {
      const franchiseId = this.effectiveFranchiseId;
      if (!franchiseId) {
        return null;
      }
      const network = await new GetPremiumNetwork().execute(franchiseId, {
        premiumStatus: this.networkFilters.premiumStatus,
        level: this.networkFilters.level,
        periodStart: this.networkFilters.periodStart || undefined,
        periodEnd: this.networkFilters.periodEnd || undefined,
      });
      this.network = network;
      return network;
    },
    async handleLoadAssociates() {
      const franchiseId = this.effectiveFranchiseId;
      if (!franchiseId) {
        this.associates = [];
        return;
      }
      try {
        this.associates = await new GetAssociates().execute(franchiseId);
      } catch {
        this.associates = [];
      }
    },
    levelColor(level: number) {
      return LEVEL_COLORS[(level - 1) % LEVEL_COLORS.length];
    },
    formatCurrency(value: number) {
      return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    formatDate(value: string) {
      try {
        return format(new Date(value), "dd/MM/yyyy");
      } catch {
        return "—";
      }
    },
    formatDocument(value: string) {
      if (!value) return "—";
      if (value.length === 11) {
        return value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      }
      if (value.length === 14) {
        return value.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
      }
      return value;
    },
    formatPhone(value: string) {
      if (!value) return "—";
      if (value.length >= 10) {
        return `(${value.slice(0, 2)}) ${value.slice(2, 7)}-${value.slice(7)}`;
      }
      return value;
    },
    formatPercent(value: number) {
      if (value == null) return "—";
      return `${value}%`;
    },
    async handlePremiumPayment() {
      const documentDigits = this.customerDocument.replace(/\D/g, "");
      if (documentDigits.length !== 11) {
        this.showSnack({ message: "Informe um CPF válido.", color: "warning" });
        return;
      }
      this.paymentLoading = true;
      try {
        const response = await new CreateAssociatePremiumSignature().execute(documentDigits);
        this.qrcodeText = buildTransactionQRCodeText(response);
        this.qrcodeImage = await QRCode.toDataURL(this.qrcodeText);
        this.showSnack({
          message: "QR Code gerado. Pague no app BRPoints.",
          color: "success",
        });
      } catch (error: unknown) {
        const err = error as { response?: { data?: { message?: string } } };
        this.showSnack({
          message: err?.response?.data?.message || "Erro ao iniciar adesão Premium.",
          color: "error",
        });
      } finally {
        this.paymentLoading = false;
      }
    },
    openNewAssociateDialog() {
      if (this.isFranchiseAssociateInactive) return;
      this.resetNewAssociate();
      this.isEditingAssociate = false;
      this.openNewAssociate = true;
    },
    resetNewAssociate() {
      this.usePremiumVoucher = false;
      this.newAssociate = {
        name: "",
        phone: "",
        email: "",
        document: "",
        transactionPercent: null,
        signaturePercent: null,
      };
    },
    handleEditAssociate(associate: AssociateType) {
      this.isEditingAssociate = true;
      this.editingAssociateId = associate.id;
      this.newAssociate = {
        name: associate.name,
        phone: associate.phone,
        email: associate.email,
        document: associate.document,
        transactionPercent: associate.transactionPercent ?? null,
        signaturePercent: associate.signaturePercent ?? null,
      };
      this.openNewAssociate = true;
    },
    closeAssociateDialog() {
      this.openNewAssociate = false;
      this.resetNewAssociate();
      this.isEditingAssociate = false;
    },
    async handleSaveAssociate() {
      if (this.isFranchiseAssociateInactive) return;
      const franchiseId = this.effectiveFranchiseId;
      if (!franchiseId) {
        this.showSnack({ message: "Franquia não identificada.", color: "warning" });
        return;
      }
      const payload: CreateAssociatePayload = {
        ...this.newAssociate,
        phone: String(this.newAssociate.phone).replace(/\D/g, ""),
        document: String(this.newAssociate.document).replace(/\D/g, ""),
        franchiseId,
        transactionPercent: this.isCollaboratorMode
          ? Number(this.newAssociate.transactionPercent ?? 0)
          : undefined,
        signaturePercent: this.isCollaboratorMode
          ? Number(this.newAssociate.signaturePercent ?? 0)
          : undefined,
        usePremiumVoucher: this.usePremiumVoucher && !this.isEditingAssociate
          ? true
          : undefined,
      };
      this.savingAssociate = true;
      try {
        if (this.isEditingAssociate && this.editingAssociateId) {
          await new UpdateAssociate().execute({
            ...payload,
            associateId: this.editingAssociateId,
          } as UpdateAssociatePayload);
        } else {
          await new CreateAssociate().execute(payload);
        }
        this.showSnack({ message: "Associado salvo com sucesso.", color: "success" });
        this.closeAssociateDialog();
        if (this.isNetworkHub) {
          await this.reloadNetworkHub();
        } else {
          await this.handleLoadAssociates();
        }
      } catch {
        this.showSnack({ message: "Não foi possível salvar o associado.", color: "error" });
      } finally {
        this.savingAssociate = false;
      }
    },
    async handleDeleteAssociate(associate: AssociateType) {
      if (!associate.id || !window.confirm(`Remover ${associate.name}?`)) return;
      try {
        await new DeleteAssociate().execute(associate.id);
        this.showSnack({ message: "Associado removido.", color: "success" });
        if (this.isNetworkHub) {
          await this.reloadNetworkHub();
        } else {
          await this.handleLoadAssociates();
        }
      } catch {
        this.showSnack({ message: "Erro ao remover.", color: "error" });
      }
    },
  },
});
</script>

<style scoped lang="scss">
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
}
.page-header__title {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  color: #1a1a2e;
}
.page-header__title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.page-header__subtitle {
  margin: 4px 0 0;
  color: #666;
  font-size: 0.9rem;
  max-width: 560px;
}
.hub-status-card__inner {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  background: linear-gradient(135deg, rgba(45, 204, 107, 0.06), rgba(229, 24, 61, 0.04));
}
.hub-status-card__title {
  font-size: 1rem;
  color: #1a1a2e;
}
.hub-status-card__detail {
  font-size: 0.8rem;
  color: #666;
  margin-top: 2px;
}
.metric-tile-card {
  text-align: center;
  height: 100%;
  transition: box-shadow 0.2s;
}
.metric-tile-card--highlight {
  border-color: #e5183d !important;
  box-shadow: 0 0 0 1px rgba(229, 24, 61, 0.2);
}
.metric-tile__locked {
  display: block;
  font-size: 0.65rem;
  color: #f57c00;
  margin-top: 4px;
}
.locked-earnings-banner {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px 16px;
  border-radius: 12px;
  background: #fff8e1;
  border: 1px solid #ffe082;
}
.locked-earnings-banner p {
  margin: 4px 0 8px;
  font-size: 0.82rem;
  color: #666;
}
.level-row__potential {
  text-align: right;
}
.level-row__potential-label {
  display: block;
  font-size: 0.65rem;
  color: #999;
  text-transform: uppercase;
}
.level-row__value--locked {
  color: #f57c00 !important;
}
.associates-page {
  padding-bottom: 72px;
}
@media (min-width: 960px) {
  .associates-page {
    padding-bottom: 0;
  }
}
.metric-tile__label {
  display: block;
  font-size: 0.72rem;
  color: #777;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.metric-tile__value {
  display: block;
  font-size: 1.1rem;
  margin-top: 4px;
  color: #1a1a2e;
}
.section-title {
  font-size: 0.95rem;
  font-weight: 700;
  margin: 0 0 4px;
}
.section-hint {
  font-size: 0.78rem;
  color: #888;
  margin: 0;
}
.level-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}
.level-row__badge {
  background: #e5183d;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  min-width: 28px;
  text-align: center;
}
.level-row__label {
  flex: 1;
  font-size: 0.85rem;
}
.level-row__detail {
  flex: 1;
  display: flex;
  flex-direction: column;
  font-size: 0.85rem;
}
.level-row__value {
  font-size: 0.9rem;
  color: #1b7d42;
}
.level-row__value--muted {
  color: #f57c00;
}
.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}
.member-name {
  font-weight: 600;
  font-size: 0.85rem;
}
.member-sub {
  font-size: 0.75rem;
  color: #888;
}
.row--premium {
  background: rgba(45, 204, 107, 0.04);
}
.earned-cell {
  font-weight: 600;
  color: #1b7d42;
  white-space: nowrap;
}
.actions {
  display: flex;
  gap: 12px;
}
.filters-card {
  background: #fafbfc;
}
.network-card {
  overflow: hidden;
}
.franchise-select {
  max-width: 320px;
}
.voucher-card {
  border-color: rgba(229, 24, 61, 0.25);
  background: linear-gradient(135deg, #fff9fa 0%, #ffffff 100%);
}
.voucher-card__title {
  display: flex;
  align-items: center;
  font-weight: 700;
  font-size: 1rem;
  color: #1a1a2e;
}
</style>
